/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkles, ShieldCheck, MapPin, BookOpen, ArrowLeft, ArrowRight,
  CheckCircle2, FileCheck2, Lock, Loader2, AlertCircle, Download,
} from "lucide-react";

export default function ExternalResearchView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await apiClient.get(`/external-research/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CONFILEX-Rapport-${(id || "").slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Rapport téléchargé");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) toast.error("Connexion requise pour télécharger le rapport.");
      else if (code === 402) toast.error("Le rapport est accessible une fois la Mission de Recherche Premium d'Spécialiste finalisée.");
      else if (code === 403) toast.error("Accès non autorisé à ce rapport.");
      else toast.error("Échec du téléchargement.");
    } finally {
      setPdfLoading(false);
    }
  };

  // Try to fetch tiers from a preview cache stored in sessionStorage (set by Orientation page)
  useEffect(() => {
    const cached = sessionStorage.getItem(`external-research-tiers-${id}`);
    if (cached) {
      try {
        setTiers(JSON.parse(cached));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("[CONFILEX] external-research tiers cache parse failed", err);
        }
      }
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`/external-research/${id}`)
      .then(r => {
        setData(r.data);
        if (r.data.pricing) setTiers(r.data.pricing);
      })
      .catch(() => toast.error("Recherche introuvable"))
      .finally(() => setLoading(false));
  }, [id]);

  const unlock = async (tier) => {
    if (!user) {
      sessionStorage.setItem("post_login_redirect", `/external-research/${id}`);
      nav("/login");
      return;
    }
    setCheckoutLoading(tier);
    try {
      const { data: r } = await apiClient.post("/external-research/checkout", {
        research_id: id, tier, origin_url: window.location.origin,
      });
      if (r.already_unlocked) {
        toast.success("Mission déjà ouverte — accès au dossier disponible");
        window.location.reload();
        return;
      }
      if (r.url) window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Le lancement de la mission a échoué");
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#FAFAF7] min-h-screen">
        <Navbar variant="light" />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
          Chargement de la recherche…
        </div>
      </div>
    );
  }

  if (!data) return null;

  const unlocked = data.unlocked;
  const previewItems = unlocked ? (data.suggestions || []) : (data.suggestions_preview || []);
  const report = data.report;

  return (
    <div className="bg-[#FAFAF7] min-h-screen flex flex-col">
      <Seo title="Recherche premium externe" noindex />
      <Navbar variant="light" />

      <main className="flex-1 max-w-4xl w-full mx-auto px-6 lg:px-10 py-10">
        <button onClick={() => nav(-1)} className="text-sm text-slate-500 hover:text-[#0B3A2A] flex items-center gap-1 mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Mission de Recherche Premium d&apos;Expert
            </div>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight text-[#0B3A2A] leading-tight mb-3">
              {unlocked ? "Mission finalisée" : "Profils identifiés pour votre dossier"}
            </h1>
            <p className="text-sm text-slate-600 max-w-2xl leading-relaxed">
              {data.suggestions_count || (data.suggestions || []).length} profils potentiellement pertinents identifiés à partir de critères objectifs et de données publiques accessibles.
            </p>
          </div>
          {unlocked && (
            <Button
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              data-testid="download-pdf-btn"
              className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-[#FAF7F1] gap-2 shrink-0"
            >
              {pdfLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</>
              ) : (
                <><Download className="w-4 h-4" /> Rapport PDF premium</>
              )}
            </Button>
          )}
        </div>

        {/* Disclaimer */}
        <Card className="p-4 rounded-2xl border-amber-200 bg-amber-50/40 mb-8" data-testid="legal-disclaimer">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 leading-relaxed">
              <strong>Mention obligatoire :</strong> {data.disclaimer}
            </div>
          </div>
        </Card>

        {/* Suggestions */}
        <div className="space-y-3 mb-10">
          {previewItems.map((s, i) => (
            <Card key={i} className={`p-5 rounded-2xl ${unlocked ? "border-slate-100" : "border-slate-100 bg-white"}`} data-testid={`research-suggestion-${i}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#C9A961]/10 border border-[#C9A961]/25 flex items-center justify-center shrink-0">
                  <span className="font-serif text-[#C9A961] text-sm">#{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <h3 className="font-serif text-lg text-[#0B3A2A]">{s.display_name}</h3>
                    <Badge className="bg-[#0B3A2A]/8 text-[#0B3A2A] border-[#0B3A2A]/20 rounded-full text-[10px] uppercase tracking-wider">
                      Profil public identifié
                    </Badge>
                    {!unlocked && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-3 flex-wrap">
                    {s.profession_label && <Badge className="bg-[#0B3A2A]/5 text-[#0B3A2A] border-slate-200 rounded-full">{s.profession_label}</Badge>}
                    {s.city_or_area && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.city_or_area}</span>}
                    {s.main_domain && <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{s.main_domain}</span>}
                  </div>
                  {unlocked ? (
                    <>
                      <p className="text-sm text-slate-700 leading-relaxed mb-3">{s.summary}</p>
                      {(s.relevance_criteria?.length > 0) && (
                        <div className="mb-3">
                          <div className="text-[10px] uppercase tracking-wider text-[#C9A961] mb-1.5">Critères de pertinence</div>
                          <ul className="space-y-1">
                            {s.relevance_criteria.map((c, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                                <CheckCircle2 className="w-3.5 h-3.5 text-[#7A9B7E] mt-0.5 shrink-0" />
                                {c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(s.public_search_hints?.length > 0) && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-[#C9A961] mb-1.5">Pistes de recherche publique</div>
                          <ul className="space-y-1">
                            {s.public_search_hints.map((h, j) => (
                              <li key={j} className="text-xs text-slate-500 italic">— {h}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {/* Affiliation: invitation CTA */}
                      <InviteExternalExpertButton
                        researchId={id}
                        suggestionIndex={i}
                        suggestion={s}
                      />
                      <p className="text-[10px] text-slate-400 italic mt-3 leading-relaxed">
                        Cette fiche est établie à partir d'informations publiques recoupées.
                        Ce professionnel n'est pas encore affilié à CONFILEX.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500 italic">{s.preview}</p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Locked: Mission de Recherche Premium d'Spécialiste — offre unique */}
        {!unlocked && tiers && (() => {
          const offer = (Array.isArray(tiers) ? tiers : Object.values(tiers))
            .find((t) => t && (t.id === "premium_expert_research" || t.label?.toLowerCase().includes("recherche premium"))) ||
            (Array.isArray(tiers) ? tiers[0] : Object.values(tiers)[0]);
          if (!offer) return null;
          const priceLabel = `${Math.round(offer.price_eur)} € TTC`;
          return (
            <>
              {/* Offre principale — Mission de Recherche Premium d'Spécialiste */}
              <Card
                className="p-6 lg:p-8 rounded-3xl border-[#C9A961]/30 bg-white mb-6"
                data-testid="pricing-panel"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Prestation CONFILEX
                </div>
                <div className="flex items-baseline gap-3 flex-wrap mb-2">
                  <h2 className="font-serif text-2xl md:text-3xl text-[#0B3A2A]">
                    Mission de Recherche Premium d&apos;Expert
                  </h2>
                  <span
                    className="font-serif text-3xl text-[#0B3A2A]"
                    data-testid="premium-mission-price"
                  >
                    {priceLabel}
                  </span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-5 max-w-3xl">
                  Vous confiez à CONFILEX une prestation institutionnelle de recherche, de
                  qualification documentaire et de transmission sécurisée vers des
                  professionnels indépendants potentiellement pertinents.
                </p>

                <div className="mb-6">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A961] mb-2.5">
                    Ce que comprend la prestation
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
                    {[
                      "Analyse de votre demande",
                      "Qualification documentaire du dossier",
                      "Identification des compétences recherchées",
                      "Recherche de professionnels adaptés",
                      "Sélection de plusieurs profils pertinents",
                      "Transmission sécurisée des éléments du dossier",
                      "Suivi de la prise de contact",
                    ].map((it) => (
                      <li key={it} className="flex items-start gap-2 leading-relaxed">
                        <CheckCircle2 className="w-4 h-4 text-[#7A9B7E] mt-0.5 shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  onClick={() => unlock(offer.id || "premium_expert_research")}
                  disabled={checkoutLoading === (offer.id || "premium_expert_research")}
                  data-testid="launch-premium-mission-btn"
                  className="rounded-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white h-12 px-7"
                >
                  {checkoutLoading === (offer.id || "premium_expert_research")
                    ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    : <Sparkles className="w-4 h-4 mr-2" />}
                  Lancer la Mission de Recherche Premium d&apos;Expert · {priceLabel}
                </Button>
                <p className="text-[11px] text-slate-500 italic mt-3 max-w-3xl leading-relaxed">
                  Paiement sécurisé Stripe. Le rapport institutionnel et les pistes
                  complètes deviennent accessibles immédiatement après finalisation.
                </p>
              </Card>

              {/* Mention de conformité obligatoire */}
              <Card
                className="p-5 lg:p-6 rounded-2xl border-[#0B3A2A]/15 bg-[#FAF7F1] mb-6"
                data-testid="compliance-notice"
              >
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-[#0B3A2A] mt-0.5 shrink-0" />
                  <div className="text-[12px] text-[#0B3A2A] leading-relaxed">
                    <strong className="font-medium">Conformité institutionnelle.</strong>{" "}
                    Le montant facturé rémunère exclusivement la prestation de recherche, de
                    qualification et de transmission réalisée par CONFILEX. Les professionnels
                    identifiés demeurent totalement indépendants dans l&apos;acceptation ou le
                    refus d&apos;un dossier. CONFILEX ne facture aucun honoraire pour le compte
                    des professionnels présentés.
                  </div>
                </div>
              </Card>

              {/* Positionnement premium */}
              <Card
                className="p-6 lg:p-7 rounded-2xl border-slate-100 bg-white mb-8"
                data-testid="premium-positioning"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2.5 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Pourquoi cette prestation est payante ?
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4 max-w-3xl">
                  Contrairement à un répertoire classique, CONFILEX ne présente pas une liste
                  générique de professionnels. Chaque demande fait l&apos;objet :
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700 mb-4">
                  {[
                    "d'une analyse individualisée",
                    "d'une qualification documentaire",
                    "d'une sélection ciblée",
                    "d'une recherche adaptée à la problématique exposée",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-2 leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C9A961] shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 italic leading-relaxed max-w-3xl">
                  L&apos;objectif est d&apos;orienter l&apos;utilisateur vers les professionnels les plus
                  pertinents au regard des informations communiquées.
                </p>
              </Card>
            </>
          );
        })()}

        {/* AI Report (unlocked) */}
        {unlocked && report && (
          <Card className="p-6 lg:p-8 rounded-3xl border-[#C9A961]/20 bg-white mb-8" data-testid="ai-report">
            <div className="flex items-center gap-2 mb-2">
              <FileCheck2 className="w-5 h-5 text-[#C9A961]" />
              <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961]">Rapport IA institutionnel</div>
            </div>
            <h2 className="font-serif text-2xl text-[#0B3A2A] mb-6">Synthèse du dossier</h2>

            {report.dossier_summary && (
              <Section title="Résumé du dossier">{report.dossier_summary}</Section>
            )}
            {report.detected_issues?.length > 0 && (
              <Section title="Problématiques organisationnelles identifiées">
                <ul className="space-y-1.5">
                  {report.detected_issues.map((x, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C9A961] shrink-0" />
                      {x}
                    </li>
                  ))}
                </ul>
              </Section>
            )}
            {report.urgency_assessment && (
              <Section title="Évaluation de l'urgence">{report.urgency_assessment}</Section>
            )}
            {report.matching_justification && (
              <Section title="Justification du matching">{report.matching_justification}</Section>
            )}
            {report.administrative_recommendations?.length > 0 && (
              <Section title="Recommandations administratives">
                <ul className="space-y-1.5">
                  {report.administrative_recommendations.map((x, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-[#7A9B7E] mt-0.5 shrink-0" />
                      {x}
                    </li>
                  ))}
                </ul>
              </Section>
            )}
            <div className="text-[11px] text-slate-500 italic mt-6 pt-5 border-t border-slate-100">{report.disclaimer}</div>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{title}</div>
      <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
    </div>
  );
}


function InviteExternalExpertButton({ researchId, suggestionIndex, suggestion }) {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | already | error
  const externalId = `ext-${(suggestion?.display_name || suggestion?.name || `sugg-${suggestionIndex}`)
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48)}`;

  const onInvite = async () => {
    setStatus("sending");
    try {
      const r = await apiClient.post(`/external-experts/${externalId}/invite`, {
        expert_external_id: externalId,
      });
      if (r.data?.already_invited) {
        setStatus("already");
        toast("Ce professionnel a déjà été invité par vos soins.");
      } else {
        setStatus("sent");
        toast.success("Invitation enregistrée — CONFILEX prendra contact avec ce professionnel.");
      }
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) toast.error("Connexion requise.");
      else if (code === 404) {
        // Le spécialiste externe n'a pas encore été enregistré côté DB ; on bascule sur recherche par
        // intent — l'admin verra la demande dans les logs.
        toast("Demande d'invitation envoyée à l'équipe CONFILEX.");
        setStatus("sent");
      } else {
        toast.error("Échec de l'invitation. Réessayez plus tard.");
        setStatus("error");
      }
    }
  };

  const isLocked = status === "sent" || status === "already";

  return (
    <div className="mt-4 pt-3 border-t border-slate-100" data-testid={`invite-external-${suggestionIndex}`}>
      <button
        onClick={onInvite}
        disabled={status === "sending" || isLocked}
        className={`inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border transition ${
          isLocked
            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : "bg-white border-[#C9A961]/40 text-[#0B3A2A] hover:bg-[#C9A961]/5 hover:border-[#C9A961]"
        }`}
      >
        {status === "sending" ? (
          <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Envoi…</>
        ) : isLocked ? (
          <><CheckCircle2 className="w-3.5 h-3.5" /> Invitation enregistrée</>
        ) : (
          <>Demander à CONFILEX d'inviter ce professionnel</>
        )}
      </button>
      {isLocked && (
        <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
          Votre demande a été prise en compte. CONFILEX peut inviter ce professionnel à rejoindre la
          plateforme afin de permettre une mise en relation sécurisée.
        </p>
      )}
    </div>
  );
}
